import { useState, useMemo, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import Images from "../../utils/Images";
import HeroBanner from "../../components/insurance/HeroBanner";
import SecondaryFilters from "../../components/insurance/SecondaryFilters";
import InsuranceGrid from "../../components/insurance/InsuranceGrid";
import useInsuranceData from "../../components/insurance/useInsuranceData";
import Footer from "../../components/landing/Footer";
import PackageDrawer from "../../components/insurance/PackageDrawer";
import AdvancedSearchDrawer from "../../components/insurance/AdvancedSearchDrawer";
import ComparisonBar from "../../components/insurance/ComparisonBar";
import ComparisonDrawer from "../../components/insurance/ComparisonDrawer";
import policyService from "../../api/PolicyService";
import { toast } from "react-toastify";
import Loader from "../../components/misc/Loader";
import { useNavigate } from "react-router-dom";
import { FileText, Heart, Home, AlertCircle, Zap, Ship, Shield as ShieldIcon, ChevronLeft } from "lucide-react";

// Hero slide for General Insurance
const generalInsuranceSlide = {
  title: "General Insurance",
  subtitle: "Protection for every part of your life",
  description: "Comprehensive general insurance coverage designed to protect against various risks and uncertainties.",
  image: Images.general_insurance,
  gradient: "from-purple-500 to-violet-600",
};

// General Insurance subcategories
const generalInsuranceSubcategories = [
  {
    key: "health_insurance",
    title: "Health Insurance",
    icon: Heart,
    gradient: "from-pink-500 to-rose-600",
    bgGradient: "from-pink-50 to-rose-50",
    iconColor: "text-pink-600",
    accentColor: "bg-pink-500",
  },
  {
    key: "home_insurance",
    title: "Home Insurance",
    icon: Home,
    gradient: "from-purple-500 to-violet-600",
    bgGradient: "from-purple-50 to-violet-50",
    iconColor: "text-purple-600",
    accentColor: "bg-purple-500",
  },
  {
    key: "accident_insurance",
    title: "Accident Insurance",
    icon: AlertCircle,
    gradient: "from-red-500 to-rose-600",
    bgGradient: "from-red-50 to-rose-50",
    iconColor: "text-red-600",
    accentColor: "bg-red-500",
  },
  {
    key: "fire_insurance",
    title: "Fire Insurance",
    icon: Zap,
    gradient: "from-orange-500 to-red-600",
    bgGradient: "from-orange-50 to-red-50",
    iconColor: "text-orange-600",
    accentColor: "bg-orange-500",
  },
  {
    key: "marine_insurance",
    title: "Marine Insurance",
    icon: Ship,
    gradient: "from-cyan-500 to-blue-600",
    bgGradient: "from-cyan-50 to-blue-50",
    iconColor: "text-cyan-600",
    accentColor: "bg-cyan-500",
  },
  {
    key: "liability_insurance",
    title: "Liability Insurance",
    icon: ShieldIcon,
    gradient: "from-teal-500 to-green-600",
    bgGradient: "from-teal-50 to-green-50",
    iconColor: "text-teal-600",
    accentColor: "bg-teal-500",
  },
];

export default function GeneralInsurance() {
  const navigate = useNavigate();
  const [selectedSubcategory, setSelectedSubcategory] = useState("health_insurance");
  const [secondaryTab, setSecondaryTab] = useState("featured");
  const [currentPage, setCurrentPage] = useState(1);
  const [isPackageOpen, setIsPackageOpen] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({});
  const isInitialMount = useRef(true);

  // Comparison feature state
  const [showCompareMode, setShowCompareMode] = useState(false);
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const MAX_COMPARISON = 3;

  // Fetch general insurance policies
  useEffect(() => {
    const fetchPolicies = async () => {
      if (isInitialMount.current) {
        setLoading(true);
      } else {
        setFilterLoading(true);
      }

      try {
        const response = await policyService.getGeneralInsurancePolicies(selectedSubcategory);
        if (response.data?.status_code === 200) {
          setPolicies(response.data.data || []);
        } else {
          toast.error(
            response.data?.reason || "An error occurred while fetching general insurance policies"
          );
          setPolicies([]);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.reason ||
            "An error occurred while fetching general insurance policies"
        );
        setPolicies([]);
      } finally {
        if (isInitialMount.current) {
          setLoading(false);
          isInitialMount.current = false;
        } else {
          setFilterLoading(false);
        }
      }
    };
    fetchPolicies();
  }, [selectedSubcategory]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Expose package drawer opener for comparison drawer
  useEffect(() => {
    window.openPolicyPackageDrawer = (item) => {
      setSelectedItem(item);
      setIsPackageOpen(true);
      setIsComparisonOpen(false);
    };
    return () => {
      delete window.openPolicyPackageDrawer;
    };
  }, []);

  // Comparison handlers
  const handleToggleCompareMode = () => {
    const newCompareMode = !showCompareMode;
    setShowCompareMode(newCompareMode);
    // Clear selections when exiting compare mode
    if (!newCompareMode) {
      setSelectedPolicies([]);
    }
  };

  const handleToggleSelect = (item) => {
    setSelectedPolicies((prev) => {
      const exists = prev.find((p) => p.id === item.id);
      if (exists) {
        return prev.filter((p) => p.id !== item.id);
      } else {
        if (prev.length >= MAX_COMPARISON) {
          toast.warning(`You can only compare up to ${MAX_COMPARISON} policies at a time`);
          return prev;
        }
        return [...prev, item];
      }
    });
  };

  const handleRemoveFromComparison = (id) => {
    setSelectedPolicies((prev) => prev.filter((p) => p.id !== id));
  };

  const handleClearComparison = () => {
    setSelectedPolicies([]);
  };

  const handleCompare = () => {
    if (selectedPolicies.length >= 2) {
      setIsComparisonOpen(true);
    } else {
      toast.info("Please select at least 2 policies to compare");
    }
  };

  // Reset page when filters change
  const resetPageOnFilterChange = (tab) => {
    if (typeof tab === "string") setSecondaryTab(tab);
    setCurrentPage(1);
  };

  const handleSubcategorySelect = (subcategory) => {
    setSelectedSubcategory(subcategory);
    setCurrentPage(1);
  };

  // Use modular data hook with real policy data and advanced filters
  const allItems = useInsuranceData(
    "general",
    secondaryTab,
    policies,
    advancedFilters
  );

  const ITEMS_PER_PAGE = 8;
  const totalPages = Math.max(1, Math.ceil(allItems.length / ITEMS_PER_PAGE));
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return allItems.slice(start, start + ITEMS_PER_PAGE);
  }, [allItems, currentPage]);

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  if (loading) {
    return <Loader fullScreen={true} text="Loading general insurance policies..." />;
  }

  return (
    <section className="overflow-hidden relative">
      {/* Hero */}
      <div className="overflow-hidden relative z-0 pt-14 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <HeroBanner slide={generalInsuranceSlide} />
        </motion.div>
      </div>

      {/* Filters */}
      <div className="relative z-10 px-4 py-6 -mt-1 bg-gradient-to-b from-white to-gray-50 sm:py-8 md:py-10 lg:py-12 sm:-mt-2 md:-mt-3 lg:-mt-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-8xl">
          <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:justify-between sm:items-end">
            <div>
              <button
                type="button"
                onClick={() => navigate("/insurance/choose")}
                className="inline-flex items-center gap-1.5 mb-3 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ChevronLeft size={16} />
                Choose different insurance
              </button>
              <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
                Select Your General Insurance Cover
              </h3>
              <div className="mt-2 w-28 h-1 bg-purple-500 rounded" />
              <p className="mt-2 text-sm text-gray-600 sm:hidden">
                Choose a category to see available policies
              </p>
            </div>
            <p className="hidden text-sm text-gray-600 lg:block">
              Choose a category to see available policies
            </p>
          </div>

          {/* Subcategory Filters */}
          <div className="mb-8">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {generalInsuranceSubcategories.map((subcategory) => {
                const Icon = subcategory.icon;
                const isSelected = selectedSubcategory === subcategory.key;
                return (
                  <motion.button
                    key={subcategory.key}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleSubcategorySelect(subcategory.key)}
                    className={`relative overflow-hidden rounded-xl border-2 p-4 text-left transition-all duration-300 ${
                      isSelected
                        ? "bg-white border-emerald-500 ring-2 shadow-lg ring-emerald-500/20"
                        : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-md"
                    }`}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <div
                        className={`p-3 rounded-lg transition-all ${
                          isSelected
                            ? "bg-gradient-to-br from-emerald-500 to-green-600 text-white"
                            : `bg-gray-100 ${subcategory.iconColor}`
                        }`}
                      >
                        <Icon className="w-6 h-6" />
                      </div>
                      <span
                        className={`text-xs font-semibold text-center transition-colors ${
                          isSelected ? "font-bold text-emerald-700" : "text-gray-700"
                        }`}
                      >
                        {subcategory.title}
                      </span>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Secondary Filters */}
          <div className="mt-8">
            <SecondaryFilters
              tab={secondaryTab}
              onTabChange={(key) => resetPageOnFilterChange(key)}
              disableOpen={isPackageOpen}
              open={isAdvancedOpen}
              onOpenChange={setIsAdvancedOpen}
              showCompareMode={showCompareMode}
              onToggleCompare={handleToggleCompareMode}
              selectedCount={selectedPolicies.length}
            />

            {/* Advanced Search Drawer */}
            <AdvancedSearchDrawer
              open={isAdvancedOpen}
              onClose={() => setIsAdvancedOpen(false)}
              onApply={(filters) => {
                setAdvancedFilters(filters);
                setCurrentPage(1);
              }}
            />

            {/* Results grid */}
            {loading || filterLoading ? (
              <div className="flex justify-center items-center py-12 mt-6">
                <div className="flex flex-col gap-4 items-center">
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full border-b-2 border-purple-500 animate-spin"></div>
                    <div className="absolute inset-0 w-8 h-8 rounded-full border-2 border-purple-200 animate-pulse"></div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {filterLoading
                      ? "Loading fresh policies..."
                      : "Loading policies..."}
                  </p>
                  {filterLoading && (
                    <p className="text-xs text-gray-500">
                      Finding the best options for you
                    </p>
                  )}
                </div>
              </div>
            ) : !selectedSubcategory ? (
              <div className="flex justify-center items-center py-12 mt-6">
                <div className="flex flex-col gap-4 items-center text-center">
                  <div className="flex justify-center items-center w-16 h-16 bg-gray-100 rounded-full">
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Select a Category
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Please select a general insurance category above to view available policies.
                    </p>
                  </div>
                </div>
              </div>
            ) : allItems.length === 0 ? (
              <div className="flex justify-center items-center py-12 mt-6">
                <div className="flex flex-col gap-4 items-center text-center">
                  <div className="flex justify-center items-center w-16 h-16 bg-gray-100 rounded-full">
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      No policies found
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      No {generalInsuranceSubcategories.find(s => s.key === selectedSubcategory)?.title.toLowerCase()} policies available at the moment. Please check back later.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div
                onClickCapture={(e) => {
                  // Prevent opening drawer when clicking compare checkbox
                  const isCompareBtn = e.target.closest('[data-compare-btn]');
                  if (isCompareBtn) return;

                  // If compare mode is active, toggle selection instead of opening drawer
                  if (showCompareMode) {
                    const card = e.target.closest("[data-card-item]");
                    if (card) {
                      const id = card.getAttribute("data-id");
                      const found = pageItems.find((i) => i.id === id);
                      if (found) {
                        handleToggleSelect(found);
                      }
                    }
                    return;
                  }

                  // Normal behavior: open drawer
                  const card = e.target.closest("[data-card-item]");
                  if (card) {
                    const id = card.getAttribute("data-id");
                    const found = pageItems.find((i) => i.id === id);
                    setSelectedItem(found || null);
                    setIsPackageOpen(true);
                    setIsAdvancedOpen(false);
                  }
                }}
              >
                <InsuranceGrid
                  items={pageItems}
                  currentPage={currentPage}
                  total={allItems.length}
                  perPage={ITEMS_PER_PAGE}
                  onPrev={handlePrev}
                  onNext={handleNext}
                  onGo={(n) => setCurrentPage(n)}
                  showCompareOption={showCompareMode}
                  selectedPolicies={selectedPolicies}
                  onToggleSelect={handleToggleSelect}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <PackageDrawer
        open={isPackageOpen}
        onClose={() => setIsPackageOpen(false)}
        item={selectedItem}
      />

      {/* Comparison Bar */}
      {showCompareMode && selectedPolicies.length >= 1 && (
        <ComparisonBar
          selectedPolicies={selectedPolicies}
          onRemove={handleRemoveFromComparison}
          onCompare={handleCompare}
          onClear={handleClearComparison}
          maxSelection={MAX_COMPARISON}
        />
      )}

      {/* Comparison Drawer */}
      <ComparisonDrawer
        open={isComparisonOpen}
        onClose={() => setIsComparisonOpen(false)}
        policies={selectedPolicies}
      />
    </section>
  );
}

